import { useCallback, useEffect, useRef, useState } from 'react';
import { apiFetch } from '../lib/api.js';
import { C, alpha } from '../lib/theme.js';
import { Button } from '../ui/ui.js';
import type { GeoResult, RegionCity, RegionTemplate } from '../lib/types.js';

/**
 * Region templates manager — Mel builds a reusable region once (name + audience
 * ANGLE + target cities); posts get "tailored" to it and re-targeted on demand.
 * The angle feeds the Copy Engine re-angle (in-brand English, not translation).
 */
export function RegionTemplatesManager({ orgId, canEdit }: { orgId: string; canEdit: boolean }) {
  const [items, setItems] = useState<RegionTemplate[]>([]);
  const [editing, setEditing] = useState<RegionTemplate | 'new' | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    if (!orgId) return;
    apiFetch<{ templates: RegionTemplate[] }>(`/api/orgs/${orgId}/region-templates`)
      .then((d) => setItems(d.templates))
      .catch((e: unknown) => setError(e instanceof Error ? e.message : 'Failed to load'));
  }, [orgId]);
  useEffect(() => load(), [load]);

  async function remove(id: string) {
    try {
      await apiFetch(`/api/orgs/${orgId}/region-templates/${id}`, { method: 'DELETE' });
      load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Delete failed');
    }
  }

  return (
    <div>
      {error && <div style={{ color: C.act, fontSize: 12.5, marginBottom: 10 }}>{error}</div>}
      {items.length === 0 && editing !== 'new' && (
        <div style={{ fontSize: 12.5, color: C.faint, marginBottom: 12 }}>
          No regions yet. Build one (e.g. &ldquo;Sydney&rdquo; with an Australian angle + its
          cities) and reuse it to tailor + re-target any post.
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {items.map((t) =>
          editing !== null && editing !== 'new' && editing.id === t.id ? (
            <RegionEditor
              key={t.id}
              orgId={orgId}
              initial={t}
              onCancel={() => setEditing(null)}
              onSaved={() => {
                setEditing(null);
                load();
              }}
            />
          ) : (
            <div
              key={t.id}
              style={{
                border: `1px solid ${C.line}`,
                borderRadius: 10,
                padding: '12px 14px',
                background: C.surface,
                display: 'flex',
                gap: 12,
                alignItems: 'flex-start',
              }}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13.5, fontWeight: 600, color: C.primary }}>{t.name}</div>
                {t.angle && (
                  <div style={{ fontSize: 12, color: C.muted, marginTop: 3, lineHeight: 1.5 }}>
                    {t.angle}
                  </div>
                )}
                {t.cities.length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginTop: 7 }}>
                    {t.cities.map((c) => (
                      <span
                        key={c.key}
                        style={{
                          font: `500 10px 'IBM Plex Mono',monospace`,
                          color: C.basil,
                          background: alpha(C.basil, 12),
                          borderRadius: 5,
                          padding: '2px 7px',
                        }}
                      >
                        {c.name}
                      </span>
                    ))}
                  </div>
                )}
              </div>
              {canEdit && (
                <div style={{ display: 'flex', gap: 12, fontSize: 11.5, flexShrink: 0 }}>
                  <span
                    onClick={() => setEditing(t)}
                    style={{ color: C.basil, cursor: 'pointer', fontWeight: 600 }}
                  >
                    Edit
                  </span>
                  <span
                    onClick={() => void remove(t.id)}
                    style={{ color: C.act, cursor: 'pointer' }}
                  >
                    Delete
                  </span>
                </div>
              )}
            </div>
          ),
        )}
      </div>

      {editing === 'new' ? (
        <div style={{ marginTop: 8 }}>
          <RegionEditor
            orgId={orgId}
            initial={null}
            onCancel={() => setEditing(null)}
            onSaved={() => {
              setEditing(null);
              load();
            }}
          />
        </div>
      ) : (
        canEdit && (
          <Button onClick={() => setEditing('new')} style={{ marginTop: 12 }}>
            + New region
          </Button>
        )
      )}
    </div>
  );
}

/** Create/edit a region — name, angle, and a city typeahead (Meta geo-search). */
function RegionEditor({
  orgId,
  initial,
  onCancel,
  onSaved,
}: {
  orgId: string;
  initial: RegionTemplate | null;
  onCancel: () => void;
  onSaved: () => void;
}) {
  const [name, setName] = useState(initial?.name ?? '');
  const [angle, setAngle] = useState(initial?.angle ?? '');
  const [cities, setCities] = useState<RegionCity[]>(initial?.cities ?? []);
  const [q, setQ] = useState('');
  const [results, setResults] = useState<GeoResult[]>([]);
  const [searching, setSearching] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const seen = useRef(new Set(cities.map((c) => c.key)));

  useEffect(() => {
    const term = q.trim();
    if (term.length < 2) {
      setResults([]);
      setSearching(false);
      return;
    }
    setSearching(true);
    const t = setTimeout(() => {
      apiFetch<{ results: GeoResult[] }>(
        `/api/orgs/${orgId}/meta/geo-search?q=${encodeURIComponent(term)}`,
      )
        .then((d) => setResults(d.results))
        .catch(() => {})
        .finally(() => setSearching(false));
    }, 250);
    return () => clearTimeout(t);
  }, [q, orgId]);

  const addCity = (g: GeoResult) => {
    if (seen.current.has(g.key)) return;
    seen.current.add(g.key);
    setCities((cs) => [...cs, { key: g.key, name: g.name }]);
    setQ('');
    setResults([]);
  };
  const removeCity = (key: string) => {
    seen.current.delete(key);
    setCities((cs) => cs.filter((c) => c.key !== key));
  };

  async function save() {
    if (!name.trim()) return;
    setBusy(true);
    setError(null);
    try {
      const body = JSON.stringify({ name: name.trim(), angle: angle.trim(), cities });
      if (initial) {
        await apiFetch(`/api/orgs/${orgId}/region-templates/${initial.id}`, {
          method: 'PATCH',
          body,
        });
      } else {
        await apiFetch(`/api/orgs/${orgId}/region-templates`, { method: 'POST', body });
      }
      onSaved();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Save failed');
      setBusy(false);
    }
  }

  const field: React.CSSProperties = {
    width: '100%',
    boxSizing: 'border-box',
    background: C.surface2,
    border: `1px solid ${C.line}`,
    borderRadius: 8,
    padding: '8px 11px',
    fontSize: 13,
    color: C.primary,
    fontFamily: 'inherit',
  };

  return (
    <div
      style={{
        border: `1px solid ${C.basil}`,
        borderRadius: 10,
        padding: 14,
        background: C.surface,
      }}
    >
      {error && <div style={{ color: C.act, fontSize: 12, marginBottom: 8 }}>{error}</div>}
      <div className="label-mono" style={{ marginBottom: 6 }}>
        Region name
      </div>
      <input
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder="e.g. Sydney"
        style={{ ...field, marginBottom: 12 }}
      />
      <div className="label-mono" style={{ marginBottom: 6 }}>
        Audience angle
      </div>
      <textarea
        value={angle}
        onChange={(e) => setAngle(e.target.value)}
        placeholder="What to emphasize for this audience — e.g. Australian winter-escape lifestyle, the short flight to warmth. (English only; the brand voice is kept.)"
        rows={2}
        style={{ ...field, marginBottom: 12, resize: 'vertical' }}
      />
      <div className="label-mono" style={{ marginBottom: 6 }}>
        Target cities
      </div>
      <div style={{ position: 'relative' }}>
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search a city to add…"
          style={field}
        />
        {q.trim().length >= 2 && (
          <div
            style={{
              position: 'absolute',
              top: 'calc(100% + 4px)',
              left: 0,
              right: 0,
              zIndex: 30,
              background: C.surface,
              border: `1px solid ${C.line}`,
              borderRadius: 9,
              overflow: 'hidden',
              maxHeight: 200,
              overflowY: 'auto',
              boxShadow: '0 12px 32px rgba(0,0,0,.4)',
            }}
          >
            {searching && results.length === 0 ? (
              <div style={{ padding: '9px 12px', color: C.muted, fontSize: 12.5 }}>Searching…</div>
            ) : results.length === 0 ? (
              <div style={{ padding: '9px 12px', color: C.muted, fontSize: 12.5 }}>No matches.</div>
            ) : (
              results.map((g) => (
                <div
                  key={g.key}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    addCity(g);
                  }}
                  style={{ padding: '9px 12px', cursor: 'pointer', fontSize: 13, color: C.primary }}
                >
                  {g.name}
                  {g.region ? `, ${g.region}` : ''}
                  {g.country_name ? (
                    <span style={{ color: C.faint }}> · {g.country_name}</span>
                  ) : null}
                </div>
              ))
            )}
          </div>
        )}
      </div>
      {cities.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
          {cities.map((c) => (
            <span
              key={c.key}
              style={{
                fontSize: 12,
                background: alpha(C.basil, 12),
                color: C.basil,
                borderRadius: 6,
                padding: '3px 9px',
              }}
            >
              {c.name}
              <span
                onClick={() => removeCity(c.key)}
                style={{ marginLeft: 6, cursor: 'pointer', opacity: 0.7 }}
              >
                ✕
              </span>
            </span>
          ))}
        </div>
      )}
      <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 14 }}>
        <Button onClick={onCancel}>Cancel</Button>
        <Button
          variant="primary"
          onClick={() => !busy && name.trim() && void save()}
          style={{ opacity: busy || !name.trim() ? 0.55 : 1 }}
        >
          {busy ? 'Saving…' : initial ? 'Save changes' : 'Create region'}
        </Button>
      </div>
    </div>
  );
}
